import { apiV2 } from './api';

export interface MsGraphStatus {
  connected: boolean;
  ms_email: string | null;
  /**
   * Hat die Verbindung den Kalender-Scope? Konten, die vor dem Kalender-Sync
   * verbunden wurden, haben ihn nicht — sie funktionieren weiter für E-Mail,
   * müssen sich für den Kalender aber neu verbinden.
   */
  calendar_enabled?: boolean;
  /**
   * Darf die Verbindung Outlook-Hauptkategorien pflegen (MailboxSettings-
   * Scope)? Ohne ihn tragen gespiegelte Termine die Kategorie nur als Namen,
   * ohne Farbe — Neu-Verbinden schaltet es frei.
   */
  categories_enabled?: boolean;
}

/** Ein Termin aus dem Outlook-Kalender (read-only im CRM). */
export interface MsCalendarEvent {
  id: string;
  title: string;
  description: string | null;
  /** Lokale Zeit in der konfigurierten Zeitzone, ohne Offset-Suffix. */
  start_at: string | null;
  end_at: string | null;
  all_day: boolean;
  is_cancelled: boolean;
  location: string | null;
  organizer: string | null;
  web_link: string | null;
  show_as: string | null;
}

export interface MsCalendarResult {
  events: MsCalendarEvent[];
  connected: boolean;
  calendarEnabled: boolean;
  /** true = Graph hat mehr Termine im Zeitraum, als geliefert wurden. */
  truncated: boolean;
}

export interface MsMessage {
  id: string;
  subject: string;
  from_name: string;
  from_email: string;
  received_at: string | null;
  preview: string;
  is_read: boolean;
  has_attachments: boolean;
  flagged: boolean;
}

export interface MsMessageDetail {
  id: string;
  subject: string;
  from_name: string;
  from_email: string;
  to: string[];
  received_at: string | null;
  body_html: string;
  body_type: 'html' | 'text';
}

export type MsFolder = 'inbox' | 'sentitems' | 'drafts' | 'archive' | 'deleteditems';

const BASE = '/admin/msgraph';

export const msgraphApi = {
  async status(): Promise<MsGraphStatus> {
    const res = await apiV2.get<{ data: MsGraphStatus }>(`${BASE}/status`);
    return res.data.data;
  },

  /** Liefert die Microsoft-Login-URL für den Connect-Button. */
  async connectUrl(): Promise<string> {
    const res = await apiV2.get<{ data: { url: string } }>(`${BASE}/connect`);
    return res.data.data.url;
  },

  async messages(folder: MsFolder = 'inbox'): Promise<MsMessage[]> {
    const res = await apiV2.get<{ data: MsMessage[] }>(`${BASE}/messages`, { params: { folder } });
    return res.data.data;
  },

  async message(id: string): Promise<MsMessageDetail> {
    const res = await apiV2.get<{ data: MsMessageDetail }>(`${BASE}/messages/${encodeURIComponent(id)}`);
    return res.data.data;
  },

  async send(to: string, subject: string, body: string): Promise<void> {
    await apiV2.post(`${BASE}/send`, { to, subject, body });
  },

  /**
   * Outlook-Termine im Zeitraum. Ohne verbundenes Konto (oder ohne
   * Kalender-Scope) kommt eine leere Liste — der Kalender bleibt benutzbar,
   * der Aufrufer kann über `connected`/`calendarEnabled` einen Hinweis zeigen.
   */
  async calendar(start: string, end: string): Promise<MsCalendarResult> {
    const res = await apiV2.get<{
      data: MsCalendarEvent[];
      meta: { connected?: boolean; calendar_enabled?: boolean; truncated?: boolean };
    }>(
      `${BASE}/calendar`,
      { params: { start, end } },
    );
    return {
      events: res.data.data ?? [],
      connected: Boolean(res.data.meta?.connected),
      calendarEnabled: Boolean(res.data.meta?.calendar_enabled),
      truncated: Boolean(res.data.meta?.truncated),
    };
  },

  async disconnect(): Promise<void> {
    await apiV2.delete(`${BASE}/disconnect`);
  },

  /** Ungelesene Mails im Posteingang — null wenn kein Konto verbunden. */
  async unread(): Promise<number | null> {
    const res = await apiV2.get<{ data: { unread: number | null } }>(`${BASE}/unread`);
    return res.data.data.unread;
  },
};
